import { forwardRef, type HTMLAttributes } from 'react'

export interface ProgressProps extends HTMLAttributes<HTMLDivElement> {
  /** How far along. A raw count, not a percentage — see `max`. */
  value: number
  /** What `value` counts against. Default 100, so a caller already holding a
   *  percentage can pass only `value`; a caller holding counts (7 of 12
   *  items done) passes both and a screen reader gets that fact rather than
   *  a flattened "58%". */
  max?: number
  /** The accessible name. There is no visible label — the bar sits next to
   *  the thing it measures — so this is the only description a screen
   *  reader gets. */
  label: string
}

/**
 * The completion bar.
 *
 * `role="progressbar"` with real `aria-valuenow`/`-min`/`-max`, not
 * `aria-hidden`: a bar that says "7 of 12" to a sighted reader and nothing to
 * everyone else only works for people who can see it.
 */
export const Progress = forwardRef<HTMLDivElement, ProgressProps>(function Progress(
  { value, max = 100, label, className = '', ...rest },
  ref,
) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0
  return (
    <div
      ref={ref}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
      className={`u-progress ${className}`.trim()}
      {...rest}
    >
      <div className="u-progress__fill" style={{ width: `${pct}%` }} />
    </div>
  )
})
